import {kstDate,TIMEZONE} from './data';

const HOUR=60*60*1000;
const DAY=24*HOUR;
const KST_OFFSET=9*HOUR;
const WEEKDAYS=['일','월','화','수','목','금','토'];
const pad=value=>String(value).padStart(2,'0');
const iso=value=>new Date(value).toISOString();
const instant=value=>typeof value==='string'&&/(?:Z|[+-]\d{2}:\d{2})$/.test(value)&&Number.isFinite(Date.parse(value));
const empty=()=>({groups:[],details:[],boundaries:[],hasMissing:true,error:'수익 추세의 조회 기간을 확인할 수 없습니다.'});
const midnight=value=>Date.parse(`${kstDate(new Date(value))}T00:00:00+09:00`);
const localParts=value=>{
 const shifted=new Date(value+KST_OFFSET);
 return {year:shifted.getUTCFullYear(),month:shifted.getUTCMonth()+1,day:shifted.getUTCDate(),hour:shifted.getUTCHours(),weekday:shifted.getUTCDay()};
};
const dateLabel=value=>{const d=localParts(value);return `${d.month}/${pad(d.day)}`;};

/**
 * Builds mobile-only trend intervals without assuming a missing bucket is zero.
 * `start`/`end` are ISO UTC instants; all interval boundaries use KST.
 * Group earnings are null for missing/ambiguous data and future periods.
 */
export function buildMobileTrend(data,period){
 if(!data || !['today','week','month'].includes(period) || data.timezone!==TIMEZONE || !Array.isArray(data.trend))return empty();
 const bucket=period==='today'?'hour':'day';
 if(data.range?.bucket!==bucket)return empty();
 if(![data.range?.start_at,data.range?.end_at,data.as_of].every(instant))return empty();
 const start=Date.parse(data.range?.start_at),end=Date.parse(data.range?.end_at),asOf=Date.parse(data.as_of);
 if(![start,end,asOf].every(Number.isFinite)||start>end||asOf<start||start!==midnight(start))return empty();
 const date=localParts(start);
 const nextYear=date.year+(date.month===12?1:0),nextMonth=date.month===12?1:date.month+1;
 const expectedEnd=period==='today'?start+DAY:period==='week'?start+7*DAY:Date.parse(`${nextYear}-${pad(nextMonth)}-01T00:00:00+09:00`);
 // The week begins on Monday; the month begins on its first day.
 if((period==='week'&&date.weekday!==1)||(period==='month'&&date.day!==1)||asOf>=expectedEnd||end>expectedEnd)return empty();
 // The documented no-transaction response has a zero total and an empty trend.
 if(data.trend.length===0&&data.summary?.transaction_count===0&&data.summary?.earnings===0)return {groups:[],details:[],boundaries:[],hasMissing:false,error:''};
 const cutoff=Math.min(asOf,end);
 const size=period==='today'?HOUR:DAY;
 const slots=new Map();
 for(const point of data.trend){
  const t=Date.parse(point?.bucket_start);
  if(!Number.isFinite(t)||t<start||t>cutoff||t>=expectedEnd)continue;
  const slot=start+Math.floor((t-start)/size)*size;
  const valid=t===slot&&Number.isSafeInteger(point.earnings);
  const previous=slots.get(slot);
  slots.set(slot,{valid:valid&&!previous,point});
 }
 const segments=[];
 if(period==='today'){
  for(let hour=0;hour<24;hour+=3)segments.push({start:start+hour*HOUR,end:start+(hour+3)*HOUR,axisStart:pad(hour),axisEnd:pad(hour+3),label:`${pad(hour)}:00~${pad(hour+3)}:00`});
 }else if(period==='week'){
  for(let i=0;i<7;i++){const from=start+i*DAY;segments.push({start:from,end:from+DAY,axisStart:WEEKDAYS[localParts(from).weekday],axisEnd:null,label:`${dateLabel(from)} (${WEEKDAYS[localParts(from).weekday]})`});}
 }else{
  for(let from=start;from<expectedEnd;from+=7*DAY){const to=Math.min(from+7*DAY,expectedEnd);segments.push({start:from,end:to,axisStart:dateLabel(from),axisEnd:dateLabel(to-DAY),label:`${dateLabel(from)}~${dateLabel(to-DAY)}`});}
 }
 const groups=segments.map(segment=>{
  const isCurrent=asOf>=segment.start&&asOf<segment.end;
  let expected=0,received=0,sum=0,overflow=false;
  for(let t=segment.start;t<segment.end&&(t<asOf||(t===asOf&&slots.has(t)));t+=size){
   expected++;
   const slot=slots.get(t);
   if(slot?.valid){received++;const next=sum+slot.point.earnings;if(!Number.isSafeInteger(next))overflow=true;else if(!overflow)sum=next;}
  }
  let status=segment.start>asOf?'future':isCurrent?'partial':'complete';
  if(expected>0&&(received!==expected||overflow))status='missing';
  if(!expected&&!isCurrent)status='future';
  return {key:iso(segment.start),...segment,start:iso(segment.start),end:iso(segment.end),earnings:expected>0&&received===expected&&!overflow?sum:null,status,isCurrent,expectedCount:expected,receivedCount:received};
 });
 let details=[...slots.entries()].filter(([t,slot])=>slot.valid&&t<=cutoff).sort(([a],[b])=>a-b).map(([t,slot])=>{
  const p=localParts(t),current=asOf>=t&&asOf<t+size;
  const label=period==='today'?`${pad(p.hour)}:00~${pad(p.hour+1)}:00`:`${dateLabel(t)} (${WEEKDAYS[p.weekday]})`;
  return {...slot.point,label,isCurrent:current};
 });
 let hasMissing=groups.some(group=>group.status==='missing');
 let error='';
 // Only compare when all elapsed intervals are present. A mismatch cannot safely be grouped.
 const values=groups.filter(group=>group.status!=='future'&&group.earnings!==null);
 if(!hasMissing&&Number.isSafeInteger(data.summary?.earnings)){
  let sum=0,overflow=false;
  for(const group of values){const next=sum+group.earnings;if(!Number.isSafeInteger(next)){overflow=true;break;}sum=next;}
  if(overflow||sum!==data.summary.earnings){
   hasMissing=true;error='수익 합계와 추세가 일치하지 않습니다.';
   details=[];
   for(const group of groups)if(group.status!=='future'){group.status='missing';group.earnings=null;}
  }
 }
 return {groups,details,boundaries:period==='today'?Array.from({length:9},(_,i)=>pad(i*3)):[],hasMissing,error};
}
